/**
 * Chart primitives for the analytics dashboards.
 *
 * Hand-rolled inline SVG rather than a charting library: the app ships no
 * charting dependency today, and these are a handful of forms (layered area,
 * grouped/stacked bar, ordinal bucket bar, cohort grid) that cost far less to
 * draw than a library costs to add to the bundle.
 *
 * Colour is NEVER hardcoded here. Every mark reads a `--chart-*` custom
 * property from index.css, which is where both themes are defined and where
 * the palette was validated against each mode's chart surface. That is what
 * makes these components theme-correct without knowing which theme is on.
 *
 * Conventions these components hold to, so the charts read as one system:
 *
 *   - Text always wears a text token (`--color-foreground` / `muted`), never a
 *     series colour. A coloured swatch next to a label carries the identity.
 *   - Grid and axis lines are recessive (`--chart-grid` / `--chart-axis`), well
 *     under the marks.
 *   - Every plotted form ships a hover layer: a crosshair + tooltip on the
 *     area chart, a per-mark tooltip on bars and cells.
 *   - Two or more series always get a legend, so identity is never colour
 *     alone, and every chart offers a table view for the same reason.
 *   - One value axis. There is deliberately no two-y-axis form in this file.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Table2, X } from "lucide-react";

import { cn } from "@/lib/utils";

/** Ordinal ramp slots. ord-1 is the least prominent step in BOTH themes. */
export const ORD = [
  "var(--chart-ord-1)",
  "var(--chart-ord-2)",
  "var(--chart-ord-3)",
  "var(--chart-ord-4)",
  "var(--chart-ord-5)",
] as const;

/** Categorical slots, assigned in fixed order — never cycled. */
export const CAT = ["var(--chart-1)", "var(--chart-2)"] as const;

export type SeriesDef = {
  key: string;
  label: string;
  color: string;
};

export type Row = Record<string, string | number | null | undefined>;

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

/** Compact axis/label formatting: 12.4k, 1.2m. Full values stay in tooltips. */
export function compact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}b`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${Math.round(n)}`;
}

/**
 * USD, pinned to en-US.
 *
 * NOT the viewer's locale: `toLocaleString` with currency USD renders "US$497"
 * under en-GB and most non-US locales, which sat next to the plain "$79/mo"
 * the rest of the app prints and read as two different currencies on the same
 * screen. The platform bills in one currency, so the format is fixed too.
 */
export function money(n: number, opts?: { compact?: boolean }): string {
  if (opts?.compact) {
    const abs = Math.abs(n);
    if (abs >= 1000) return `$${compact(n)}`;
  }
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Width of a container, tracked live.
 *
 * The SVGs are drawn at real pixel width rather than scaled with a viewBox:
 * a `preserveAspectRatio="none"` viewBox would stretch strokes and text along
 * with the geometry, which is exactly the distortion the mark specs forbid.
 */
function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width];
}

/** "Nice" axis maximum, so ticks land on readable numbers instead of 8317. */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(value)));
  const scaled = value / pow;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return step * pow;
}

function ticksFor(max: number, count = 4): number[] {
  // On a small axis (a DAU chart topping out at 2) four evenly spaced ticks are
  // 0/0.5/1/1.5/2, and the integer formatter renders that as "0, 1, 1, 2, 2" —
  // a gridline labelled twice, which reads as a rendering bug. Below the tick
  // count, step by whole numbers so every label is distinct.
  if (max <= count) {
    return Array.from({ length: Math.max(2, Math.round(max) + 1) }, (_, i) => i);
  }
  return Array.from({ length: count + 1 }, (_, i) => (max / count) * i);
}

// ── Shared chrome ────────────────────────────────────────────────────

function Legend({ series }: { series: SeriesDef[] }) {
  // Present whenever there is more than one series: identity must never rest
  // on colour alone. A single-series chart is named by its own title instead.
  if (series.length < 2) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {series.map((s) => (
        <span key={s.key} className="flex items-center gap-2 text-base text-muted-foreground">
          <span
            aria-hidden
            className="h-3 w-3 shrink-0 rounded-[3px]"
            style={{ background: s.color }}
          />
          {s.label}
        </span>
      ))}
    </div>
  );
}

/**
 * Title + legend + the table-view escape hatch that every chart carries.
 *
 * The table is not a nicety: several palette steps sit under 3:1 against their
 * surface, which the validator allows only where the values are readable some
 * other way. This is that other way.
 */
export function ChartFrame({
  title,
  subtitle,
  series,
  rows,
  columns,
  actions,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  series?: SeriesDef[];
  rows?: Row[];
  columns?: { key: string; label: string; format?: (v: unknown, row: Row) => string }[];
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const [showTable, setShowTable] = useState(false);
  const canTable = Boolean(rows?.length && columns?.length);

  return (
    <section
      className={cn(
        "rounded-2xl border border-border/80 bg-card p-5 shadow-card sm:p-6",
        className,
      )}
    >
      <div className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {/* `heading-bold`, not `font-bold`: the unlayered `h1,h2,h3` rule in
              index.css pins every heading to 600 and beats Tailwind's layered
              font-* utilities, so `font-bold` alone renders at the same weight
              as the body copy under it. */}
          <h3 className="heading-bold text-xl tracking-tight text-foreground">{title}</h3>
          {subtitle && (
            <p className="mt-1.5 text-base leading-6 text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          {canTable && (
            <button
              type="button"
              onClick={() => setShowTable((v) => !v)}
              aria-pressed={showTable}
              title={showTable ? "Show chart" : "Show data table"}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/80 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              {showTable ? <X className="h-4 w-4" /> : <Table2 className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {series && series.length > 1 && (
        <div className="pb-3">
          <Legend series={series} />
        </div>
      )}

      {showTable && canTable ? (
        <div className="max-h-[22rem] overflow-auto rounded-xl border border-border/70">
          <table className="w-full text-left text-base">
            <thead className="sticky top-0 bg-muted/60 text-muted-foreground">
              <tr>
                {columns!.map((c) => (
                  <th key={c.key} className="px-3 py-2.5 font-semibold">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows!.map((r, i) => (
                <tr key={i} className="border-t border-border/60">
                  {columns!.map((c) => (
                    <td key={c.key} className="px-3 py-2 text-foreground">
                      {c.format ? c.format(r[c.key], r) : String(r[c.key] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        children
      )}
    </section>
  );
}

// ── Tooltip ──────────────────────────────────────────────────────────

function Tooltip({
  x,
  y,
  width,
  title,
  entries,
}: {
  x: number;
  y: number;
  width: number;
  title: string;
  entries: { label: string; value: string; color?: string }[];
}) {
  // Flip to the left of the cursor near the right edge so the tooltip never
  // gets clipped by the panel.
  const flip = x > width - 150;
  return (
    <div
      className="pointer-events-none absolute z-20 min-w-[11rem] rounded-lg border border-border/80 bg-popover px-3.5 py-2.5 shadow-lg"
      style={{
        left: flip ? x - 150 : x + 12,
        top: Math.max(0, y - 12),
      }}
    >
      <div className="text-base font-semibold text-foreground">{title}</div>
      <div className="mt-1 space-y-0.5">
        {entries.map((e) => (
          <div key={e.label} className="flex items-center justify-between gap-4 text-base">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              {e.color && (
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                  style={{ background: e.color }}
                />
              )}
              {e.label}
            </span>
            <span className="font-semibold tabular-nums text-foreground">{e.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Layered area chart ───────────────────────────────────────────────

/**
 * Layered areas over a shared x axis, with a crosshair.
 *
 * Built for NESTED series — DAU inside WAU inside MAU — where the containment
 * is a fact of the data, so the overlap is the point rather than an accident.
 * Series are drawn in the order given, so the caller passes the largest first
 * and it sits at the back. Colours come from the ordinal ramp: the nesting has
 * an order, and the ramp is what puts that order in the colour.
 */
export function AreaTrendChart({
  data,
  xKey,
  series,
  height = 260,
  formatValue = compact,
  formatTooltipValue,
  formatX,
  formatTooltipX,
  labelFontSize = 16,
}: {
  data: Row[];
  xKey: string;
  series: SeriesDef[];
  height?: number;
  formatValue?: (n: number) => string;
  /** Tooltip value text. Defaults to the plain localized number every existing
   * caller already gets. */
  formatTooltipValue?: (n: number) => string;
  formatX?: (v: string) => string;
  formatTooltipX?: (v: string) => string;
  /** Default 16 — unchanged for every existing caller. A narrower host (e.g.
   * a chat panel) can pass a smaller size so axis text doesn't crowd itself
   * out; the label-thinning logic below still adapts to whatever's left. */
  labelFontSize?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const pad = { top: 14, right: 14, bottom: 32, left: 56 };
  const plotW = Math.max(0, width - pad.left - pad.right);
  const plotH = Math.max(0, height - pad.top - pad.bottom);

  const max = useMemo(
    () =>
      niceMax(
        Math.max(1, ...data.flatMap((d) => series.map((s) => num(d[s.key])))),
      ),
    [data, series],
  );

  const xAt = useCallback(
    (i: number) => (data.length <= 1 ? plotW / 2 : (plotW * i) / (data.length - 1)),
    [data.length, plotW],
  );
  const yAt = useCallback((v: number) => plotH - (num(v) / max) * plotH, [plotH, max]);

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const rel = e.clientX - rect.left - pad.left;
    if (data.length === 0 || plotW <= 0) return;
    const step = data.length <= 1 ? plotW : plotW / (data.length - 1);
    const idx = Math.max(0, Math.min(data.length - 1, Math.round(rel / step)));
    setHover(idx);
  };

  if (data.length === 0) {
    return (
      <div ref={ref} className="flex items-center justify-center text-base text-muted-foreground" style={{ height }}>
        No activity recorded in this range yet.
      </div>
    );
  }

  const hoveredRow = hover !== null ? data[hover] : null;

  return (
    <div
      ref={ref}
      className="relative"
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
    >
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Activity trend">
          <g transform={`translate(${pad.left},${pad.top})`}>
            {ticksFor(max).map((t) => (
              <g key={t}>
                <line
                  x1={0}
                  x2={plotW}
                  y1={yAt(t)}
                  y2={yAt(t)}
                  stroke="var(--chart-grid)"
                  strokeWidth={1}
                />
                <text
                  x={-8}
                  y={yAt(t)}
                  dy="0.32em"
                  textAnchor="end"
                  className="fill-[var(--color-muted-foreground)]"
                  style={{ fontSize: labelFontSize }}
                >
                  {formatValue(t)}
                </text>
              </g>
            ))}

            {series.map((s) => {
              const line = data
                .map((d, i) => `${i === 0 ? "M" : "L"}${xAt(i)},${yAt(num(d[s.key]))}`)
                .join(" ");
              const area = `${line} L${xAt(data.length - 1)},${plotH} L${xAt(0)},${plotH} Z`;
              return (
                <g key={s.key}>
                  <path d={area} fill={s.color} fillOpacity={0.16} />
                  <path
                    d={line}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                </g>
              );
            })}

            {hover !== null && (
              <g>
                <line
                  x1={xAt(hover)}
                  x2={xAt(hover)}
                  y1={0}
                  y2={plotH}
                  stroke="var(--chart-axis)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
                {series.map((s) => (
                  <circle
                    key={s.key}
                    cx={xAt(hover)}
                    cy={yAt(num(data[hover][s.key]))}
                    r={4.5}
                    fill={s.color}
                    /* 2px surface ring keeps overlapping markers separable. */
                    stroke="var(--color-card)"
                    strokeWidth={2}
                  />
                ))}
              </g>
            )}

            <line
              x1={0}
              x2={plotW}
              y1={plotH}
              y2={plotH}
              stroke="var(--chart-axis)"
              strokeWidth={1}
            />

            {data.map((d, i) => {
              // Thin the x labels to whatever fits, so they never collide.
              const every = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(plotW / 78))));
              const isLast = i === data.length - 1;
              const onStep = i % every === 0;
              if (!onStep && !isLast) return null;
              // The final label is drawn even when it is off-step, so the axis
              // always ends on a real date. Suppressed when the gap to the
              // previous step label is too small to fit both — that overlap
              // rendered as "7 Sept 8 Sept" printed on top of each other.
              if (isLast && !onStep && (data.length - 1) % every < every * 0.6) return null;
              return (
                <text
                  key={i}
                  x={xAt(i)}
                  y={plotH + 20}
                  /* First and last anchor inward so neither is clipped by the
                     plot edge. */
                  textAnchor={i === 0 ? "start" : isLast ? "end" : "middle"}
                  className="fill-[var(--color-muted-foreground)]"
                  style={{ fontSize: labelFontSize }}
                >
                  {formatX ? formatX(String(d[xKey])) : String(d[xKey])}
                </text>
              );
            })}
          </g>
        </svg>
      )}

      {hoveredRow && (
        <Tooltip
          x={pad.left + xAt(hover!)}
          y={pad.top + 8}
          width={width}
          title={
            formatTooltipX
              ? formatTooltipX(String(hoveredRow[xKey]))
              : String(hoveredRow[xKey])
          }
          entries={series.map((s) => ({
            label: s.label,
            value: formatTooltipValue
              ? formatTooltipValue(num(hoveredRow[s.key]))
              : num(hoveredRow[s.key]).toLocaleString(),
            color: s.color,
          }))}
        />
      )}
    </div>
  );
}

// ── Bar chart (single or stacked) ────────────────────────────────────

/**
 * Vertical bars over a categorical/period x axis, optionally stacked.
 *
 * Stacked segments are separated by a 2px surface-coloured gap rather than a
 * stroke, so adjacent fills stay distinguishable without adding a line weight
 * that competes with the data. Data-ends are rounded 4px at the top only —
 * the bar stays anchored to the baseline.
 */
export function BarChart({
  data,
  xKey,
  series,
  height = 240,
  formatValue = compact,
  formatTooltipValue,
  formatX,
  formatTooltipX,
  labelFontSize = 16,
}: {
  data: Row[];
  xKey: string;
  series: SeriesDef[];
  height?: number;
  formatValue?: (n: number) => string;
  formatTooltipValue?: (n: number) => string;
  formatX?: (v: string) => string;
  formatTooltipX?: (v: string) => string;
  /** Default 16 — unchanged for every existing caller. See AreaTrendChart's
   * same prop for why a narrower host might want a smaller value. */
  labelFontSize?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const pad = { top: 14, right: 14, bottom: 32, left: 62 };
  const plotW = Math.max(0, width - pad.left - pad.right);
  const plotH = Math.max(0, height - pad.top - pad.bottom);

  const totals = useMemo(
    () => data.map((d) => series.reduce((sum, s) => sum + num(d[s.key]), 0)),
    [data, series],
  );
  const max = useMemo(() => niceMax(Math.max(1, ...totals)), [totals]);

  const slot = data.length > 0 ? plotW / data.length : 0;
  const barW = Math.max(2, Math.min(38, slot * 0.62));
  const fmtTip = formatTooltipValue ?? ((n: number) => n.toLocaleString());

  if (data.length === 0) {
    return (
      <div ref={ref} className="flex items-center justify-center text-base text-muted-foreground" style={{ height }}>
        Nothing to show for this range.
      </div>
    );
  }

  return (
    <div ref={ref} className="relative" onMouseLeave={() => setHover(null)}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Bar chart">
          <g transform={`translate(${pad.left},${pad.top})`}>
            {ticksFor(max).map((t) => (
              <g key={t}>
                <line
                  x1={0}
                  x2={plotW}
                  y1={plotH - (t / max) * plotH}
                  y2={plotH - (t / max) * plotH}
                  stroke="var(--chart-grid)"
                  strokeWidth={1}
                />
                <text
                  x={-8}
                  y={plotH - (t / max) * plotH}
                  dy="0.32em"
                  textAnchor="end"
                  className="fill-[var(--color-muted-foreground)]"
                  style={{ fontSize: labelFontSize }}
                >
                  {formatValue(t)}
                </text>
              </g>
            ))}

            {data.map((d, i) => {
              const cx = slot * i + slot / 2;
              let cursor = plotH;
              return (
                <g key={i}>
                  {/* Hit target spans the whole slot, so hovering never
                      requires landing on a 3px bar. */}
                  <rect
                    x={slot * i}
                    y={0}
                    width={slot}
                    height={plotH}
                    fill="transparent"
                    onMouseEnter={() => setHover(i)}
                  />
                  {series.map((s, si) => {
                    const v = num(d[s.key]);
                    if (v <= 0) return null;
                    const h = (v / max) * plotH;
                    // 2px surface gap between stacked segments.
                    const gap = si > 0 ? 2 : 0;
                    const y = cursor - h;
                    cursor = y - gap;
                    const isTop = si === series.length - 1 || series.slice(si + 1).every((o) => num(d[o.key]) <= 0);
                    return (
                      <rect
                        key={s.key}
                        x={cx - barW / 2}
                        y={y}
                        width={barW}
                        height={Math.max(1, h)}
                        rx={isTop ? 4 : 0}
                        fill={s.color}
                        opacity={hover === null || hover === i ? 1 : 0.45}
                      />
                    );
                  })}
                </g>
              );
            })}

            <line x1={0} x2={plotW} y1={plotH} y2={plotH} stroke="var(--chart-axis)" strokeWidth={1} />

            {data.map((d, i) => {
              const every = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(plotW / 78))));
              const isLast = i === data.length - 1;
              const onStep = i % every === 0;
              if (!onStep && !isLast) return null;
              // Same collision guard as the area chart above.
              if (isLast && !onStep && (data.length - 1) % every < every * 0.6) return null;
              return (
                <text
                  key={i}
                  x={slot * i + slot / 2}
                  y={plotH + 20}
                  textAnchor={i === 0 ? "start" : isLast ? "end" : "middle"}
                  className="fill-[var(--color-muted-foreground)]"
                  style={{ fontSize: labelFontSize }}
                >
                  {formatX ? formatX(String(d[xKey])) : String(d[xKey])}
                </text>
              );
            })}
          </g>
        </svg>
      )}

      {hover !== null && data[hover] && (
        <Tooltip
          x={pad.left + slot * hover + slot / 2}
          y={pad.top + 8}
          width={width}
          title={
            formatTooltipX
              ? formatTooltipX(String(data[hover][xKey]))
              : String(data[hover][xKey])
          }
          entries={series.map((s) => ({
            label: s.label,
            value: fmtTip(num(data[hover]![s.key])),
            color: s.color,
          }))}
        />
      )}
    </div>
  );
}

// ── Ordinal bucket bar ───────────────────────────────────────────────

/**
 * One horizontal bar split into ordered buckets (recency, engagement depth).
 *
 * The buckets have an inherent order, so they take the ordinal ramp and the
 * reader sees the progression in the colour. Every segment is direct-labelled
 * underneath, which is also what satisfies the relief rule for the lighter
 * ramp steps.
 */
export function BucketBar({
  buckets,
  total,
}: {
  buckets: { label: string; value: number; hint?: string }[];
  total?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const sum = total ?? buckets.reduce((a, b) => a + b.value, 0);
  if (sum <= 0) {
    return <p className="text-base text-muted-foreground">No accounts to categorise yet.</p>;
  }
  return (
    <div>
      <div className="flex h-7 w-full gap-[2px] overflow-hidden rounded-lg">
        {buckets.map((b, i) => {
          const pct = (b.value / sum) * 100;
          if (pct <= 0) return null;
          return (
            <div
              key={b.label}
              title={`${b.label}: ${b.value.toLocaleString()}`}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              className="h-full transition-opacity"
              style={{
                width: `${pct}%`,
                background: ORD[Math.min(i, ORD.length - 1)],
                opacity: hover === null || hover === i ? 1 : 0.5,
              }}
            />
          );
        })}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
        {buckets.map((b, i) => (
          <div key={b.label} className="flex items-center justify-between gap-3 text-base">
            <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
              <span
                aria-hidden
                className="h-3 w-3 shrink-0 rounded-[3px]"
                style={{ background: ORD[Math.min(i, ORD.length - 1)] }}
              />
              <span className="truncate">{b.label}</span>
            </span>
            <span className="shrink-0 font-semibold tabular-nums text-foreground">
              {b.value.toLocaleString()}
              <span className="ml-1 font-normal text-muted-foreground">
                {((b.value / sum) * 100).toFixed(0)}%
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Horizontal ranked bars ───────────────────────────────────────────

/**
 * A ranked list where the bar length is the value — for nominal breakdowns
 * (role, plan, organization).
 *
 * Every bar takes the SAME slot-1 hue on purpose: these are one series, and
 * colouring each row differently would spend the identity channel re-encoding
 * what the bar length already shows.
 */
export function RankedBars({
  rows,
  formatValue = (n: number) => n.toLocaleString(),
  emptyLabel = "Nothing to show.",
  wrapLabels = false,
}: {
  rows: { label: string; value: number }[];
  formatValue?: (n: number) => string;
  emptyLabel?: string;
  /** Default false, unchanged for every existing caller: a long label
   * truncates to one line with an ellipsis. Opt in to let it wrap onto a
   * second line instead — reads better than a mid-word ellipsis for
   * genuinely long labels (e.g. a listing title) in a narrow container,
   * where there's already enough row height to spare. */
  wrapLabels?: boolean;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (rows.length === 0) {
    return <p className="text-base text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="flex items-baseline justify-between gap-3 pb-1">
            <span
              className={cn(
                "text-base font-medium text-foreground",
                wrapLabels ? "line-clamp-2 break-words" : "truncate",
              )}
            >
              {r.label}
            </span>
            <span className="shrink-0 text-base font-semibold tabular-nums text-foreground">
              {formatValue(r.value)}
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted/60">
            <div
              className="h-full rounded-full"
              style={{ width: `${(r.value / max) * 100}%`, background: "var(--chart-1)" }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Cohort retention grid ────────────────────────────────────────────

/**
 * Signup cohorts down, weeks-since-signup across, cell shaded by retention.
 *
 * Magnitude within a single hue, so this is a sequential ramp — the ordinal
 * steps reused as one, with the percentage printed in every cell so the value
 * never depends on reading the shade.
 */
export function CohortGrid({
  cohorts,
  weeks,
}: {
  cohorts: { cohort_week: string; size: number; cells: { week: number; pct: number; retained: number }[] }[];
  weeks: number;
}) {
  if (cohorts.length === 0) {
    return <p className="text-base text-muted-foreground">No signup cohorts in this window yet.</p>;
  }
  const shade = (pct: number): string => {
    if (pct <= 0) return "transparent";
    const idx = Math.min(ORD.length - 1, Math.floor((pct / 100) * ORD.length));
    return ORD[idx];
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[40rem] border-separate border-spacing-[3px] text-center text-base">
        <thead>
          <tr>
            <th className="px-2 py-1 text-left font-semibold text-muted-foreground">Cohort</th>
            <th className="px-2 py-1 font-semibold text-muted-foreground">Size</th>
            {Array.from({ length: weeks }, (_, w) => (
              <th key={w} className="px-1 py-1 font-semibold text-muted-foreground">
                W{w}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((c) => (
            <tr key={c.cohort_week}>
              <td className="whitespace-nowrap px-2 py-1 text-left font-medium text-foreground">
                {new Date(`${c.cohort_week}T00:00:00Z`).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  timeZone: "UTC",
                })}
              </td>
              <td className="px-2 py-1 tabular-nums text-muted-foreground">{c.size}</td>
              {c.cells.map((cell) => (
                <td
                  key={cell.week}
                  title={`${cell.retained} of ${c.size} active in week ${cell.week}`}
                  className="rounded-[4px] px-1 py-1 tabular-nums text-foreground"
                  style={{
                    background: shade(cell.pct),
                    // Ink stays a text token; the cell shade carries magnitude.
                    opacity: cell.pct <= 0 ? 0.35 : 1,
                  }}
                >
                  {cell.pct > 0 ? `${Math.round(cell.pct)}%` : "–"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Stat tile ────────────────────────────────────────────────────────

/**
 * A headline number with an optional comparison and sparkline.
 *
 * The single most common right answer in this whole file: when the data's job
 * is to deliver one number, a chart is the wrong form.
 */
export function StatTile({
  label,
  value,
  hint,
  delta,
  deltaLabel,
  spark,
  tone = "default",
  invertDelta = false,
}: {
  label: string;
  value: string;
  hint?: string;
  /** Previous-period value. Omit entirely when no comparison is available. */
  delta?: { current: number; previous: number } | null;
  deltaLabel?: string;
  spark?: number[];
  tone?: "default" | "gold";
  /** For metrics where DOWN is good (failed payments, refunds). */
  invertDelta?: boolean;
}) {
  const pct =
    delta && delta.previous > 0
      ? ((delta.current - delta.previous) / delta.previous) * 100
      : null;
  const rising = pct !== null && pct > 0;
  const good = pct === null ? null : invertDelta ? !rising : rising;

  return (
    <div
      className={cn(
        "rounded-xl border p-4 shadow-sm",
        tone === "gold"
          ? "border-primary/30 bg-primary/[0.06]"
          : "border-border/75 bg-card",
      )}
    >
      <div className="text-sm font-bold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 flex items-end justify-between gap-3">
        <div className="text-3xl font-extrabold tracking-tight text-foreground">{value}</div>
        {spark && spark.length > 1 && <Sparkline values={spark} />}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 text-base">
        {pct !== null && (
          <span
            className={cn(
              "font-semibold tabular-nums",
              good ? "text-emerald-500" : "text-rose-500",
            )}
          >
            {rising ? "▲" : "▼"} {Math.abs(pct).toFixed(pct % 1 === 0 ? 0 : 1)}%
          </span>
        )}
        {(deltaLabel || hint) && (
          <span className="text-muted-foreground">{pct !== null ? deltaLabel : hint}</span>
        )}
      </div>
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const w = 62;
  const h = 22;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const d = values
    .map((v, i) => {
      const x = (w * i) / (values.length - 1);
      const y = h - ((v - min) / span) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} aria-hidden className="shrink-0">
      <path d={d} fill="none" stroke="var(--chart-1)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ── Range filter ─────────────────────────────────────────────────────

/** Single row of range choices, sitting above the charts they control. */
export function RangePicker<T extends string | number>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  label?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      {label && (
        <span className="text-sm font-bold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
      )}
      <div className="inline-flex rounded-lg border border-border/80 bg-card p-0.5">
        {options.map((o) => (
          <button
            key={String(o.value)}
            type="button"
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
            className={cn(
              "rounded-[0.4rem] px-3.5 py-1.5 text-base font-semibold transition-colors",
              value === o.value
                ? "bg-gold-gradient text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The controls row, on its own surface BELOW the page hero.
 *
 * Deliberately not inside the hero's `actions` slot: the hero is a dark
 * banner with a lattice/glow treatment, and range tabs sitting on top of it
 * read as decoration rather than as controls — hard to see and easy to miss.
 * A plain card directly above the charts is also where the interaction
 * guidance puts filters, so the two agree.
 */
export function FilterBar({
  children,
  meta,
}: {
  children: ReactNode;
  /** Right-hand status text — "Updated 2m ago", freshness, record counts. */
  meta?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border/80 bg-card px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <div className="flex flex-wrap items-center gap-3">{children}</div>
      {meta && (
        <div className="flex items-center gap-3 text-base text-muted-foreground">{meta}</div>
      )}
    </div>
  );
}

/** Relative "updated 2m ago" label, ticking on its own. */
export function useAgo(iso: string | null | undefined): string {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return "";
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 45) return "just now";
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}
